/**
 * featureFlags.ts — FEATURE FLAGS™ (OPERATOR EXPERIENCE™ Phase 8, Build 2.14.0)
 *
 * Launch gating for social/collaborative surfaces that are not ready for
 * public beta. Flags default to "coming-soon" and can be activated instantly
 * (Dev Center → Commercial™) without a rebuild — state lives in localStorage
 * and changes broadcast via a window event so mounted UI updates live.
 *
 * HONESTY CONTRACT: a gated surface must render an explicit "Coming Soon"
 * state — never fabricated content, never silently hidden functionality that
 * the UI still advertises as working.
 */

export type FeatureFlagId =
  | 'community'
  | 'operator-chat'
  | 'reviews'
  | 'ratings'
  | 'collaboration';

export type FeatureFlagStatus = 'enabled' | 'coming-soon';

export interface FeatureFlagDef {
  id: FeatureFlagId;
  label: string;
  description: string;
  /** Launch default — what a fresh install shows. */
  defaultStatus: FeatureFlagStatus;
}

export const FEATURE_FLAGS_KEY = 'nomad:feature-flags:v1';
export const FLAGS_CHANGED_EVENT = 'nomad:flags-changed';

export const FEATURE_FLAG_DEFS: FeatureFlagDef[] = [
  {
    id: 'community',
    label: 'Community™',
    description: 'Nearby Roamers, community tips, and shared local knowledge.',
    defaultStatus: 'coming-soon',
  },
  {
    id: 'operator-chat',
    label: 'Operator Chat™',
    description: 'Direct messaging between operators.',
    defaultStatus: 'coming-soon',
  },
  {
    id: 'reviews',
    label: 'Reviews™',
    description: 'Operator-written reviews of platforms and places. (Curated, source-attributed reviews remain visible — this gates operator-generated reviews.)',
    defaultStatus: 'coming-soon',
  },
  {
    id: 'ratings',
    label: 'Ratings™',
    description: 'Public operator ratings and leaderboards.',
    defaultStatus: 'coming-soon',
  },
  {
    id: 'collaboration',
    label: 'Future Collaboration™',
    description: 'Joint work sessions and shared missions between operators.',
    defaultStatus: 'coming-soon',
  },
];

export const ALL_FEATURE_FLAG_IDS: FeatureFlagId[] = FEATURE_FLAG_DEFS.map(d => d.id);

type FlagOverrides = Partial<Record<FeatureFlagId, FeatureFlagStatus>>;

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

/** Read persisted overrides; corrupt/unknown entries are dropped, never throw. */
export function loadFlagOverrides(): FlagOverrides {
  if (!hasLocalStorage()) return {};
  try {
    const raw = localStorage.getItem(FEATURE_FLAGS_KEY);
    if (!raw) return {};
    const p = JSON.parse(raw) as Record<string, unknown> | null;
    if (!p || typeof p !== 'object') return {};
    const out: FlagOverrides = {};
    for (const id of ALL_FEATURE_FLAG_IDS) {
      const v = p[id];
      if (v === 'enabled' || v === 'coming-soon') out[id] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function getFlagStatus(id: FeatureFlagId): FeatureFlagStatus {
  const override = loadFlagOverrides()[id];
  if (override) return override;
  return FEATURE_FLAG_DEFS.find(d => d.id === id)?.defaultStatus ?? 'coming-soon';
}

export function isFeatureEnabled(id: FeatureFlagId): boolean {
  return getFlagStatus(id) === 'enabled';
}

/** Dev Center: flip a flag instantly. Broadcasts so mounted UI re-renders. */
export function setFlagStatus(id: FeatureFlagId, status: FeatureFlagStatus): void {
  const overrides = loadFlagOverrides();
  overrides[id] = status;
  if (hasLocalStorage()) {
    try { localStorage.setItem(FEATURE_FLAGS_KEY, JSON.stringify(overrides)); } catch { /* quota */ }
  }
  if (typeof window !== 'undefined') {
    try { window.dispatchEvent(new CustomEvent(FLAGS_CHANGED_EVENT)); } catch { /* SSR/test */ }
  }
}

/** Dev Center: back to launch defaults. */
export function resetFlags(): void {
  if (hasLocalStorage()) {
    try { localStorage.removeItem(FEATURE_FLAGS_KEY); } catch { /* ignore */ }
  }
  if (typeof window !== 'undefined') {
    try { window.dispatchEvent(new CustomEvent(FLAGS_CHANGED_EVENT)); } catch { /* SSR/test */ }
  }
}

export function getAllFlagStatuses(): Record<FeatureFlagId, FeatureFlagStatus> {
  const out = {} as Record<FeatureFlagId, FeatureFlagStatus>;
  for (const id of ALL_FEATURE_FLAG_IDS) out[id] = getFlagStatus(id);
  return out;
}
